import {
  AvatarGroup,
  AvatarWithTooltip,
  Button,
  BetaDataTableCell as Cell,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";
import { useTranslations } from "@zapier/i18n";
import styles from "./AccessCell.module.css";
import { ConnectionGroup, ConnectionMember, Member } from "@zapier/bff";
import { getInitials } from "../../../utils";
import { Icon } from "@zapier/zinnia-icons";

interface Props {
  groups: ConnectionGroup[];
  members: ConnectionMember[];
  owner: Member | null | undefined;
  sharedWithEveryoneGroup?: ConnectionGroup | null;
  onShare: () => void;
  isShareDisabled?: boolean;
}

export function AccessCell({
  groups,
  members,
  owner,
  sharedWithEveryoneGroup,
  onShare,
  isShareDisabled = false,
}: Props) {
  const t = useTranslations("connections-table");
  const notShared =
    !sharedWithEveryoneGroup && !owner && !groups.length && !members.length;

  if (sharedWithEveryoneGroup) {
    return (
      <Cell className={styles.root}>
        <AvatarGroup size="small">
          <AvatarWithTooltip
            key="workspace"
            initials={getInitials(sharedWithEveryoneGroup.name ?? "")}
            name={t("tooltips.shared-with-everyone-tooltip", {
              workspace: sharedWithEveryoneGroup.name ?? "",
            })}
            url={sharedWithEveryoneGroup.photoUrl}
          />
        </AvatarGroup>
      </Cell>
    );
  }

  if (notShared) {
    return (
      <Cell className={styles.root}>
        <div className={styles.notShared}>
          <Tooltip
            allowMultiline
            content={t("tooltips.not-shared-tooltip")}
            placement="bottom"
          >
            <TooltipFocusableTrigger>
              <div aria-label={t("tooltips.not-shared-tooltip")} role="img">
                <Icon
                  name="alertInfo"
                  size={16}
                  isBlock
                  color="StrokeStronger"
                />
              </div>
            </TooltipFocusableTrigger>
          </Tooltip>
          <Button
            variant="tertiary"
            size="compact"
            isDisabled={isShareDisabled}
            onClick={onShare}
          >
            {t("buttons.share-connection")}
          </Button>
        </div>
      </Cell>
    );
  }

  return (
    <Cell className={styles.root}>
      <AvatarGroup size="small">
        {!!owner && (
          <AvatarWithTooltip
            key="owner"
            initials={getInitials(owner.fullName)}
            name={`${owner.fullName || ""} is the owner`}
            url={owner.photoUrl}
          />
        )}
        {groups.map((group, index) => (
          <AvatarWithTooltip
            key={index}
            name={group.name || ""}
            url={group.photoUrl}
          />
        ))}
        {members.map((member, index) => (
          <AvatarWithTooltip
            key={index}
            name={member.fullName || ""}
            url={member.photoUrl}
          />
        ))}
      </AvatarGroup>
    </Cell>
  );
}
